import * as React from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreVertical,
  Play,
  Eye,
  Pencil,
  Trash2,
  Users,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  BarChart3,
} from 'lucide-react'
import type { WACampaign, WACampaignStatus } from '@/lib/api-client'

interface CampaignCardProps {
  campaign: WACampaign
  onView: () => void
  onEdit: () => void
  onDelete: () => void
  onExecute: () => void
  onViewAnalytics: () => void
}

const statusConfig: Record<WACampaignStatus, { label: string; variant: 'default' | 'secondary' | 'success' | 'destructive' | 'warning'; icon: React.ReactNode }> = {
  draft: {
    label: 'Draft',
    variant: 'secondary',
    icon: <Pencil className="w-3 h-3" />,
  },
  scheduled: {
    label: 'Scheduled',
    variant: 'warning',
    icon: <Clock className="w-3 h-3" />,
  },
  in_progress: {
    label: 'Active',
    variant: 'default',
    icon: <Loader2 className="w-3 h-3 animate-spin" />,
  },
  completed: {
    label: 'Completed',
    variant: 'success',
    icon: <CheckCircle2 className="w-3 h-3" />,
  },
  cancelled: {
    label: 'Cancelled',
    variant: 'destructive',
    icon: <XCircle className="w-3 h-3" />,
  },
}

export function CampaignCard({
  campaign,
  onView,
  onEdit,
  onDelete,
  onExecute,
  onViewAnalytics,
}: CampaignCardProps) {
  const status = statusConfig[campaign.status]
  const canEdit = campaign.status === 'draft'
  const canExecute = campaign.status === 'draft' || campaign.status === 'scheduled'
  const hasResults = campaign.sentCount > 0

  // Calculate percentages
  const deliveryRate = campaign.sentCount > 0
    ? Math.round((campaign.deliveredCount / campaign.sentCount) * 100)
    : 0
  const readRate = campaign.deliveredCount > 0
    ? Math.round((campaign.readCount / campaign.deliveredCount) * 100)
    : 0

  return (
    <div className="flex flex-col p-4 rounded-lg border border-border bg-card hover:border-border/80 transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-text-dark truncate" title={campaign.name}>
            {campaign.name}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <Badge
              variant={status.variant === 'warning' ? 'outline' : status.variant === 'success' ? 'default' : status.variant}
              className={cn(
                'flex items-center gap-1 text-[10px] font-medium',
                status.variant === 'warning' && 'bg-warning/10 text-warning border-warning/30',
                status.variant === 'success' && 'bg-success text-white'
              )}
            >
              {status.icon}
              {status.label}
            </Badge>
            {campaign.templateName && (
              <span className="text-[11px] text-text-muted truncate max-w-[120px]" title={campaign.templateName}>
                {campaign.templateName}
              </span>
            )}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onView}>
              <Eye className="w-4 h-4 mr-2" />
              View
            </DropdownMenuItem>
            {hasResults && (
              <DropdownMenuItem onClick={onViewAnalytics}>
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </DropdownMenuItem>
            )}
            {canEdit && (
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
            )}
            {canExecute && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onExecute} className="text-primary">
                  <Play className="w-4 h-4 mr-2" />
                  Execute
                </DropdownMenuItem>
              </>
            )}
            {canEdit && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDelete} className="text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/30">
          <Users className="w-3.5 h-3.5 text-text-muted" />
          <div className="text-xs">
            <span className="font-medium text-text-dark">{campaign.totalRecipients}</span>
            <span className="text-text-muted ml-1">recipients</span>
          </div>
        </div>
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/30">
          <Send className="w-3.5 h-3.5 text-text-muted" />
          <div className="text-xs">
            <span className="font-medium text-text-dark">{campaign.sentCount}</span>
            <span className="text-text-muted ml-1">sent</span>
          </div>
        </div>
      </div>

      {/* Progress bars (only show if has results) */}
      {hasResults && (
        <div className="space-y-2 mb-3">
          {/* Delivery rate */}
          <div>
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="text-text-muted">Delivered</span>
              <span className="text-text-dark font-medium">{deliveryRate}%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${deliveryRate}%` }}
              />
            </div>
          </div>
          {/* Read rate */}
          <div>
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="text-text-muted">Read</span>
              <span className="text-text-dark font-medium">{readRate}%</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-success rounded-full transition-all"
                style={{ width: `${readRate}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-border/50 mt-auto">
        <span className="text-[10px] text-text-muted">
          {new Date(campaign.createdAt).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </span>
        {campaign.failedCount > 0 && (
          <span className="text-[10px] text-destructive flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            {campaign.failedCount} failed
          </span>
        )}
      </div>
    </div>
  )
}
